/**
 * Firestore utility functions
 */

/**
 * Remove undefined values from object before saving to Firestore
 * Firestore doesn't accept undefined, only null or omitted fields
 *
 * @param data - Object to clean
 * @returns Object with undefined values removed
 */
export const cleanFirestoreData = <T extends Record<string, any>>(data: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  ) as Partial<T>;
};

/**
 * Deep clean nested objects for Firestore
 * Removes undefined values at all levels
 */
export const deepCleanFirestoreData = <T extends Record<string, any>>(data: T): Partial<T> => {
  const cleaned: any = {};

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) {
      continue; // Skip undefined values
    }

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively clean nested objects
      cleaned[key] = deepCleanFirestoreData(value);
    } else {
      cleaned[key] = value;
    }
  }

  return cleaned as Partial<T>;
};
